import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { fadeIn, noTransform } from '../../layout/keyframes';
import { ButtonReset } from '../../layout/reset';

const btnFontSize = (props: any) => props.theme.font.size.small;
const btnLineHeight = (props: any) => props.theme.font.lineHeight.main;
const btnIconSize = (props: any) => props.theme.font.size.regular;

export const Wrapper = styled.div.withConfig({ componentId: 'sc-kyzkrw-0' })`
    position: relative;
    display: inline-block;
`;

export const FoldoutDialog = styled.div.withConfig({ componentId: 'sc-kyzkrw-1' })`
    background-color: var(--c-bg-main);
    box-shadow: ${(props) => props.theme.boxShadow.strong};
    transform: translateY(${size(-2)});
    animation: ${noTransform} 200ms ease forwards, ${fadeIn} 200ms ease;
    width: 250px;
    max-width: calc(100vw - ${size(6)});
    border-radius: 3px;
`;

const posXStartStyle = css`
    left: 0;
    right: auto;
`;

const posXEndStyle = css`
    left: auto;
    right: 0;
`;

const posYTopStyle = css`
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: ${size(0.5)};

    ${FoldoutDialog} {
        transform: translateY(${size(2)});
    }
`;

const posYBottomStyle = css`
    top: 100%;
    bottom: auto;
    margin-top: ${size(0.5)};
    margin-bottom: 0;

    ${FoldoutDialog} {
        transform: translateY(${size(-2)});
    }
`;

interface FoldoutProps {
  $posX?: 'start' | 'end';
  $posXSm?: 'start' | 'end';
  $posXMd?: 'start' | 'end';
  $posY?: 'top' | 'bottom';
  $posYSm?: 'top' | 'bottom';
  $posYMd?: 'top' | 'bottom';
}

export const Foldout = styled.div.withConfig({ componentId: 'sc-kyzkrw-2' })<FoldoutProps>`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: ${size(0.5)};
    z-index: 20;

    ${({ $posX }) =>
      $posX === 'start'
        ? css`
                  ${posXStartStyle}
              `
        : $posX === 'end'
          ? css`
                  ${posXEndStyle}
              `
          : undefined}

    ${({ $posXSm }) =>
      $posXSm === 'start'
        ? css`
                  ${(props) => props.theme.breakpoint.Sm} {
                      ${posXStartStyle}
                  }
              `
        : $posXSm === 'end'
          ? css`
                  ${(props) => props.theme.breakpoint.Sm} {
                      ${posXEndStyle}
                  }
              `
          : undefined}

  ${({ $posXMd }) =>
    $posXMd === 'start'
      ? css`
                  ${(props) => props.theme.breakpoint.Md} {
                      ${posXStartStyle}
                  }
              `
      : $posXMd === 'end'
        ? css`
                  ${(props) => props.theme.breakpoint.Md} {
                      ${posXEndStyle}
                  }
              `
        : undefined}

  ${({ $posY }) =>
    $posY === 'top'
      ? css`
                  ${posYTopStyle}
              `
      : $posY === 'bottom'
        ? css`
                  ${posYBottomStyle}
              `
        : undefined}

  ${({ $posYSm }) =>
    $posYSm === 'top'
      ? css`
                  ${(props) => props.theme.breakpoint.Sm} {
                      ${posYTopStyle}
                  }
              `
      : $posYSm === 'bottom'
        ? css`
                  ${(props) => props.theme.breakpoint.Sm} {
                      ${posYBottomStyle}
                  }
              `
        : undefined}

  ${({ $posYMd }) =>
    $posYMd === 'top'
      ? css`
                  ${(props) => props.theme.breakpoint.Md} {
                      ${posYTopStyle}
                  }
              `
      : $posYMd === 'bottom'
        ? css`
                  ${(props) => props.theme.breakpoint.Md} {
                      ${posYBottomStyle}
                  }
              `
        : undefined}
`;

export const FoldoutUiBlock = styled.div.attrs(() => ({
  role: 'button',
})).withConfig({ componentId: 'sc-kyzkrw-3' })`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
`;

export const FoldoutList = styled.ul.withConfig({ componentId: 'sc-kyzkrw-4' })`
    list-style: none;
    padding: ${size(1)} 0;
    margin: 0;

    & + & {
        position: relative;
        margin-top: 1px;

        &:before {
            content: "";
            position: absolute;
            top: -1px;
            left: ${size(3)};
            right: ${size(3)};
            height: 1px;
            background-color: var(--c-border-main);
        }
    }
`;

export const FoldoutItem = styled.li.withConfig({ componentId: 'sc-kyzkrw-5' })``;

interface FoldoutBtnProps {
  $tone?: 'link' | 'error';
}

export const FoldoutBtn = styled(ButtonReset).withConfig({ componentId: 'sc-kyzkrw-6' })<FoldoutBtnProps>`
    display: flex;
    align-items: center;
    width: 100%;
    padding: ${size(1)} ${size(3)};
    text-align: left;
    color: var(--c-text-main);
    font-weight: var(--font-weight-regular);
    font-size: ${btnFontSize};
    line-height: ${btnLineHeight};

    &:hover:not([disabled]) {
        background-color: var(--c-bg-alt);
    }

    &[disabled] {
        opacity: 0.37;
        cursor: not-allowed;
    }

    ${({ $tone }) =>
      $tone === 'link'
        ? css`
                  &:not([disabled]) {
                      color: var(--palette-primary-500);
                  }
              `
        : $tone === 'error'
          ? css`
                  color: var(--c-semantic-error-500);
              `
          : undefined}
`;

export const FoldoutBtnContent = styled.span.withConfig({ componentId: 'sc-kyzkrw-7' })`
    display: block;

    & + & {
        margin-left: ${size(0.5)};
    }
`;

export const FoldoutBtnIcon = styled.span.withConfig({ componentId: 'sc-kyzkrw-8' })`
    align-self: flex-start;
    margin-right: ${size(1)};
    font-size: ${btnIconSize};
    margin-top: calc((${btnIconSize} - ${btnFontSize} * ${btnLineHeight}) / -2);
`;
